import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SiteFrame from "./SiteFrame";
import { AppStoreButton } from "./AppStore";
import { PhoneFrame, useAppShot } from "./appScreens";
import { APP_PATH, FIND_PATH, STUDIO_APP } from "./paths";
import { ProductBadge, SearchIcon } from "./products";
import { StudioDemo, StudioFeatures, StudioHero, StudioTestimonials } from "./StudioPage";
import { StudioPillars, StudioPlans } from "./StudioSections";
import { BAND, EYEBROW, H2, TEXT_LINK, WRAP } from "./ui";

/** The participants' line: "Looking for your event photos? Find them →" (/find-your-photos, a Studio feature). */
export const FindLine = () => {
  const { t } = useTranslation();
  return (
    <p className="m-0 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-2xl border border-border bg-muted px-5 py-4 text-base text-foreground" data-testid="find-line">
      <span className="flex items-center gap-2.5 font-semibold">
        <span className="text-accent-foreground">
          <SearchIcon size={20} />
        </span>
        {t("home.story.find.question")}
      </span>
      <Link to={FIND_PATH} className={`${TEXT_LINK} font-bold`}>
        {t("home.story.find.link")} <span aria-hidden="true">→</span>
      </Link>
    </p>
  );
};

/**
 * "For your own photos": the home page's ONE band for Photohawk Friends & Family (owner, 2 Nov 2026; 3 Nov 2026: named
 * by who it's for, with one line saying it's separate from Studio, so Studio members don't take it for their app). Lower down, after
 * Studio's story: what the app does in one line, the App Store button, "Android: coming soon" in words (no Google Play
 * badge) and "Learn more" (/app). Marked as the app's (data-product="personal", the App badge), so it never reads as a
 * Studio feature. Never shown inside Studio itself (the admin, galleries, emails).
 */
export const AppBand = () => {
  const { t } = useTranslation();
  const band = (key: string) => t(`home.band.${key}`);
  const peopleShot = useAppShot("people");
  const shareShot = useAppShot("share");
  return (
    <section aria-labelledby="home-app-band" className={`${BAND} border-y border-border`} data-testid="home-app-band" data-product="personal">
      <div className={`${WRAP} flex flex-wrap items-center gap-x-14 gap-y-10 py-[clamp(48px,6vw,80px)]`}>
        <div className="flex min-w-0 flex-[1.2_1_420px] flex-col gap-5">
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-2 self-start">
            <ProductBadge product="personal" />
            <span className={EYEBROW}>{band("eyebrow")}</span>
          </span>
          <h2 id="home-app-band" className={H2}>
            {band("title")}
          </h2>
          <p className="m-0 max-w-[30em] text-[clamp(18px,1.6vw,21px)] font-semibold leading-snug text-foreground">{band("text")}</p>
          <p className="m-0 max-w-[34em] text-base leading-relaxed text-muted-foreground">{band("detail")}</p>
          <p className="m-0 max-w-[34em] text-sm font-semibold leading-relaxed text-foreground" data-testid="home-app-separate">
            {band("separate")}
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3 pt-1" data-testid="home-app-band-actions">
            <AppStoreButton testId="home-app-store" />
            <Link to={APP_PATH} className={TEXT_LINK} data-testid="home-app-learn-more">
              {band("learnMore")} <span aria-hidden="true">→</span>
              <span className="sr-only">: {band("title")}</span>
            </Link>
          </div>
          <p className="m-0 text-sm font-semibold text-muted-foreground" data-testid="home-app-android">
            {band("android")}
          </p>
        </div>
        {/* The app itself: its People and Share screens. */}
        <div className="relative flex min-w-0 flex-[1_1_300px] justify-center gap-[6%] px-2">
          <div className="w-[min(40%,190px)] -rotate-[4deg]">
            <PhoneFrame shot={peopleShot} alt={t("home.personal.screens.alt.people")} sizes="190px" />
          </div>
          <div className="mt-8 w-[min(40%,190px)] rotate-[3deg]">
            <PhoneFrame shot={shareShot} alt={t("home.personal.screens.alt.share")} sizes="190px" />
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * The home page (staging only until launch: src/lib/homeFlag.ts). One brand, one story, Studio first (owner, 2 Nov
 * 2026): the hero, value, proof, the plans and the main call to action are Photohawk Studio's, for photographers and
 * event organisers, as on today's home page (its translated copy). Participants get the Find your photos line. Then,
 * lower down, one band for the Photohawk app (AppBand), so the app is there without splitting the page in two.
 */
const HomePage = () => (
  <SiteFrame page="home" path="home">
    <StudioHero appHref={STUDIO_APP} />
    <StudioPillars />
    <StudioFeatures />
    <StudioDemo />
    <StudioTestimonials />
    <StudioPlans />
    <div className={`${WRAP} pb-[clamp(48px,6vw,80px)]`}>
      <FindLine />
    </div>
    <AppBand />
  </SiteFrame>
);

export default HomePage;
